import * as React from 'react';
import { connect } from 'react-redux';

import { IUser } from '../../types/user';
import { IAnswer } from '../../types/answer';
import { Poll as PollComponent } from '../../components/poll/poll';
import { loadPollInfo, submitPoll } from '../../reducers/modules/poll/poll';
import { TDispatch } from '../../types/redux';

interface IProps {
  type: string;
  objectID: string;
  user?: IUser;
  dispatch: TDispatch;
}

interface IContainerProps {
  type: string;
  objectID: string;
}

interface IPollState {
  id: number;
  answers: IAnswer[];
  title: string;
  isMultiAnswer: boolean;
  isUserVote: boolean;
  userVote: number[];
  answerCount: number;
}

const initialState: IPollState = {
  id: 0,
  answers: [],
  title: '',
  isMultiAnswer: false,
  isUserVote: false,
  userVote: [],
  answerCount: 0,
};

const PollContainer: React.FC<IProps> = ({ type, objectID, user, dispatch }) => {
  const [pollState, setPollState] = React.useState<IPollState>(initialState);

  React.useEffect(() => {
    dispatch(loadPollInfo(type, objectID, (data) => {
      setPollState({
        id: data.id,
        answers: data.answers,
        title: data.title,
        isMultiAnswer: data.isMultiAnswer,
        isUserVote: data.userVote.length !== 0 || data.status !== 'active',
        userVote: data.userVote,
        answerCount: data.answerCount,
      });
    }, () => {
      setPollState(initialState);
    }) as never);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countNewAnswer = (answers: IAnswer[], answerCount: number, userVote: number[]): IAnswer[] => {
    return answers.map(answer => {
      const updated = { ...answer };
      if (userVote.includes(updated['id'])) {
        updated['count'] += 1;
      }
      updated['percent'] = Math.round(updated['count'] / answerCount * 100);
      return updated;
    });
  };

  const handleVotePressed = (idVote: number) => {
    setPollState(prev => {
      const newUserVote = prev.userVote.includes(idVote)
        ? prev.userVote.filter(vote => vote !== idVote)
        : [...prev.userVote, idVote];

      const updated = { ...prev, userVote: newUserVote };

      if (!prev.isMultiAnswer) {
        dispatch(submitPoll(type, objectID, prev.id, [idVote], (resultVote: number[]) => {
          const answerCount = prev.answerCount + 1;
          setPollState(s => ({
            ...s,
            userVote: resultVote,
            isUserVote: true,
            answers: countNewAnswer(s.answers, answerCount, resultVote),
            answerCount,
          }));
        }, () => {}) as never);
      }

      return updated;
    });
  };

  const handleSubmitPressed = () => {
    dispatch(submitPoll(type, objectID, pollState.id, pollState.userVote, (userVote: number[]) => {
      const answerCount = pollState.answerCount + pollState.userVote.length;
      setPollState(prev => ({
        ...prev,
        userVote,
        isUserVote: true,
        answers: countNewAnswer(prev.answers, answerCount, userVote),
        answerCount,
      }));
    }, () => {}) as never);
  };

  return (
    <PollComponent
      answers={pollState.answers}
      answerCount={pollState.answerCount}
      title={pollState.title}
      isMultiAnswer={pollState.isMultiAnswer}
      isUserVote={pollState.isUserVote}
      onVotePressed={handleVotePressed}
      onSubmitPressed={handleSubmitPressed}
      type={type}
      userVote={pollState.userVote}
      user={user}
    />
  );
};

export const Poll = connect<IContainerProps, {}, IContainerProps>(
  (state: { user: IUser }, props: IContainerProps) => ({
    user: state.user,
    type: props.type,
    objectID: props.objectID,
  }),
)(PollContainer);
